/**
 * Personal ledger — an employee's awards, their vesting movements and the
 * taxable gains derived from them.
 *
 * The ledger endpoint answers one shape per tab, keyed by `LedgerType`
 * (1 = award detail, 2 = vesting detail, 3 = taxable gains), each alongside the
 * same `planData` header the page builds its filters from.
 */

import { TOUR_AWARDS } from './tour-sample-awards';
import { TOUR_PRIMARY_EMPLOYEE, TourEmployee } from './tour-sample-people';

export interface TourLedgerAwardRow {
  id: number;
  awardNo: string;
  planName: string;
  awardDate: string;
  awardYear: number;
  grantYear: number;
  performancePeriod: string;
  awardShares: number;
  vestedShares: number;
  unVestedShares: number;
  lapsedShares: number;
  adjustedShares: number;
  vestingSchedule: string;
  mode: string;
  status: string;
}

export interface TourLedgerVestingRow {
  id: number;
  awardNo: string;
  planName: string;
  tranche: number;
  vestingDate: string;
  vestedShares: number;
  releasedShares: number;
  releaseDate: string;
  marketPrice: number;
  mode: string;
  status: string;
}

export interface TourLedgerTaxableGainRow {
  id: number;
  awardNo: string;
  planName: string;
  vestingDate: string;
  listingDate: string;
  taxableShares: number;
  totalTaxableShares: number;
  closingPrice: number;
  gains: number;
  totalGains: number;
  taxableGains: number;
  mode: string;
}

// Keyed by plan name, which is data rather than an identifier — a Map keeps
// those keys out of the object-literal naming rules.
const MODE_BY_PLAN = new Map<string, string>([
  ['Performance Share Plan', 'Share'],
  ['Restricted Share Plan', 'Share'],
  ['Employee Share Option Scheme', 'Share'],
  ['Phantom Cash Plan', 'Cash'],
]);

/** Awards belonging to one employee — the ledger is always person-scoped. */
const awardsFor = (employee: TourEmployee) => {
  const owned = TOUR_AWARDS.filter(
    award => award.employeeId === employee.employeeId
  );
  // Every demo employee should have something to look at, so anyone without
  // their own award borrows the primary one.
  return owned.length ? owned : [TOUR_AWARDS[0]];
};

export const getTourLedgerAwardRows = (
  employee: TourEmployee = TOUR_PRIMARY_EMPLOYEE
): TourLedgerAwardRow[] =>
  awardsFor(employee).map((award, index) => {
    const vested = award.vestingSchedule[0].sharesVested;
    const lapsed = award.statusName === 'Lapsed' ? award.awardedShares : 0;
    return {
      id: 9100 + index,
      awardNo: award.awardNo,
      planName: award.planName,
      awardDate: award.awardDate,
      awardYear: Number(award.awardDate.slice(0, 4)),
      grantYear: Number(award.awardDate.slice(0, 4)),
      performancePeriod: award.performancePeriod,
      awardShares: award.awardedShares,
      vestedShares: lapsed ? 0 : vested,
      unVestedShares: lapsed ? 0 : award.awardedShares - vested,
      lapsedShares: lapsed,
      adjustedShares: 0,
      vestingSchedule: '3-Year Graded (33/33/34)',
      mode: MODE_BY_PLAN.get(award.planType) ?? 'Share',
      status: award.statusName,
    };
  });

export const getTourLedgerVestingRows = (
  employee: TourEmployee = TOUR_PRIMARY_EMPLOYEE
): TourLedgerVestingRow[] =>
  awardsFor(employee).flatMap((award, awardIndex) =>
    award.vestingSchedule.map((tranche, trancheIndex) => ({
      id: 9200 + awardIndex * 10 + trancheIndex,
      awardNo: award.awardNo,
      planName: award.planName,
      tranche: tranche.sNo,
      vestingDate: tranche.vestingDate,
      vestedShares: tranche.sharesVested,
      releasedShares: tranche.sNo === 1 ? tranche.sharesVested : 0,
      releaseDate: tranche.sNo === 1 ? tranche.vestingDate : '',
      marketPrice: Number((4.6 + trancheIndex * 0.11).toFixed(2)),
      mode: MODE_BY_PLAN.get(award.planType) ?? 'Share',
      status: tranche.sNo === 1 ? 'Released' : 'Unvested',
    }))
  );

export const getTourLedgerTaxableGainRows = (
  employee: TourEmployee = TOUR_PRIMARY_EMPLOYEE
): TourLedgerTaxableGainRow[] =>
  awardsFor(employee).map((award, index) => {
    const tranche = award.vestingSchedule[0];
    const price = 4.82;
    const gains = Number((tranche.sharesVested * price).toFixed(2));
    return {
      id: 9300 + index,
      awardNo: award.awardNo,
      planName: award.planName,
      vestingDate: tranche.vestingDate,
      listingDate: tranche.vestingDate,
      taxableShares: tranche.sharesVested,
      totalTaxableShares: tranche.sharesVested,
      closingPrice: price,
      gains,
      totalGains: gains,
      taxableGains: gains,
      mode: MODE_BY_PLAN.get(award.planType) ?? 'Share',
    };
  });

/** The plan filters shown above the ledger tabs. */
export const getTourLedgerPlanData = (
  employee: TourEmployee = TOUR_PRIMARY_EMPLOYEE
) => {
  const awards = awardsFor(employee);
  const planTypes = [
    ...new Map(
      awards.map(award => [
        award.planTypeId,
        {
          planType: award.planType,
          planTypeCode: award.planType
            .split(' ')
            .map(word => word[0])
            .join(''),
          planTypeID: award.planTypeId,
        },
      ])
    ).values(),
  ];

  const planNames = [
    ...new Map(
      awards.map(award => [
        award.planName,
        {
          planName: award.planName,
          planNameID: award.awardListId,
          planTypeID: award.planTypeId,
        },
      ])
    ).values(),
  ];

  return { planTypes, planNames };
};

/** The `LedgerType`-keyed payload the ledger endpoint answers with. */
export const getTourLedgerPayload = (
  ledgerType: number,
  employee: TourEmployee = TOUR_PRIMARY_EMPLOYEE
) => {
  const header = {
    fullName: employee.fullName,
    employeeID: employee.employeeId,
    // Taxable Gains is a key-management-only tab; the demo participant is one
    // so the tour can visit every tab.
    isKeyManagement: true,
    planData: getTourLedgerPlanData(employee),
  };

  if (ledgerType === 2) {
    return {
      ...header,
      ledgerAwardVestingDetail: getTourLedgerVestingRows(employee),
    };
  }
  if (ledgerType === 3) {
    return {
      ...header,
      ledgerTaxableGainDetail: getTourLedgerTaxableGainRows(employee),
    };
  }
  return { ...header, ledgerAwardDetail: getTourLedgerAwardRows(employee) };
};
